import { ProjectAPI } from 'api/project';
import { projectCollection } from './project-collection';
/**
 * Centralized READ wrappers over `ProjectAPI` for CORE project reads. Consumers
 * call these instead of `ProjectAPI.*` directly so every fetched `Project`
 * flows through `projectCollection.push(...)`, while the original API result is
 * returned unchanged.
 *
 * Fidelity rule (see `project-collection.ts`):
 * - the single **detail** fetch is authoritative → push with the default
 *   replace mode (and gen-guarded against out-of-order refetches);
 * - every **list / paginated** fetch is lower-fidelity → push INSERT-ONLY
 *   (`pushNewProjects`): seed projects not yet in the collection, but never
 *   overwrite an existing (detail-fetched or optimistically-edited) record. A
 *   shallow merge would otherwise replace a detail record's rich nested fields
 *   (e.g. `projectPhases` carrying `project`) with the list's thinner version
 *   and corrupt downstream consumers (the gantt phase list crashed on a phase
 *   missing `.project`).
 */ /**
 * Seed only projects NOT already in the collection — list/paginated fetches must
 * not downgrade a richer existing record (see fidelity rule above).
 */ const pushNewProjects = (projects)=>{
    const fresh = projects.filter((p)=>!projectCollection.has(p.projectId));
    if (fresh.length > 0) {
        projectCollection.push(fresh, {
            mode: 'merge'
        });
    }
};
/**
 * Per-project detail refetch generation. Rapid edits each trigger a WS refetch;
 * those can resolve out of order and Project has no monotonic version to dedupe
 * by, so a late STALE refetch would push an old snapshot and flicker. Stamp each
 * `fetchProjectDetail` call and only push if it is still the latest in-flight
 * refetch for that project. Mirrors `taskFetchGen` in `collections/tasks`.
 */ const projectFetchGen = new Map();
/** GET full project detail → push (replace), gen-guarded. */ export const fetchProjectDetail = async (...args)=>{
    var _projectFetchGen_get;
    const [projectId] = args;
    const gen = ((_projectFetchGen_get = projectFetchGen.get(projectId)) !== null && _projectFetchGen_get !== void 0 ? _projectFetchGen_get : 0) + 1;
    projectFetchGen.set(projectId, gen);
    const result = await ProjectAPI.getProjectDetails(...args);
    if (projectFetchGen.get(projectId) === gen) {
        projectCollection.push([
            result
        ]);
    }
    return result;
};
/** GET light project list → insert-only seed, return raw. */ export const fetchProjectsLight = async ()=>{
    const result = await ProjectAPI.getProjectsLight();
    pushNewProjects(result);
    return result;
};
/** GET project meta list → insert-only seed, return raw. */ export const fetchProjectsMeta = async ()=>{
    const result = await ProjectAPI.getProjectsMeta();
    pushNewProjects(result);
    return result;
};
/** GET archived project list → insert-only seed, return raw. */ export const fetchArchivedProjects = async ()=>{
    const result = await ProjectAPI.getArchivedProjectList();
    pushNewProjects(result);
    return result;
};
/** GET deleted project list → insert-only seed, return raw. */ export const fetchDeletedProjects = async ()=>{
    const result = await ProjectAPI.getDeletedProjectList();
    pushNewProjects(result);
    return result;
};
/** GET projects for a customer → insert-only seed, return raw. */ export const fetchProjectsByCustomer = async (...args)=>{
    const result = await ProjectAPI.getProjectsByCustomer(...args);
    pushNewProjects(result);
    return result;
};
/** GET a paginated/filtered project page → insert-only seed rows, return raw. */ export const fetchPaginatedProjects = async (...args)=>{
    const result = await ProjectAPI.getPaginatedProject(...args);
    pushNewProjects(result.data);
    return result;
};
/** POST a paginated/filtered project page → insert-only seed rows, return raw. */ export const postPaginatedProjects = async (...args)=>{
    const result = await ProjectAPI.postPaginatedProject(...args);
    pushNewProjects(result.data);
    return result;
};
